import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { getProject, getNextProject } from '../data/projects';
import { useMeta } from '../hooks/useMeta';
import ProjectViewer from '../components/ProjectViewer';
import { atterrir } from '../lib/transitionProjet';
import '../styles/project.css';

const ProjectPage = () => {
  const { slug } = useParams();
  const project = getProject(slug);
  useMeta(
    project && {
      title: `${project.title} — ${project.subtitle}`,
      description: project.summary,
      image: project.image,
      path: `/projets/${slug}`
    }
  );

  const viewerRef = useRef(null);

  useEffect(() => {
    // Le `scroll-behavior: smooth` de base.css s'applique à toutes les API de
    // défilement, scrollTop compris : le retour en haut s'étalait sur 600 ms et
    // la cible du vol était mesurée en pleine course, donc au mauvais endroit.
    // On neutralise le temps du saut, et on rétablit une fois celui-ci appliqué,
    // pour que les ancres de la page d'accueil restent douces.
    const racine = document.documentElement;
    racine.style.scrollBehavior = 'auto';
    racine.scrollTop = 0;
    document.body.scrollTop = 0;

    const id = requestAnimationFrame(() => {
      racine.style.scrollBehavior = '';
      atterrir(viewerRef.current);
    });
    return () => {
      cancelAnimationFrame(id);
      racine.style.scrollBehavior = '';
    };
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  const next = getNextProject(slug);

  return (
    <main className="project">
      <div className="shell">
        <Link to="/#work" className="project-back">
          <ArrowLeft size={15} aria-hidden="true" />
          Tous les projets
        </Link>

        <header className="project-head">
          <p className="project-kicker">{project.subtitle}</p>
          <h1 className="project-title">{project.title}</h1>
          <p className="project-summary">{project.summary}</p>
        </header>

        <div ref={viewerRef}>
          <ProjectViewer project={project} />
        </div>

        {/* Fiche technique */}
        <div className="fiche">
          <section className="fiche-tile fiche-tile--wide">
            <h2 className="fiche-label">Le projet</h2>
            <p className="fiche-text">{project.description}</p>
          </section>

          <section className="fiche-tile">
            <h2 className="fiche-label">Repères</h2>
            <dl className="fiche-specs">
              <div>
                <dt>Année</dt>
                <dd>{project.year}</dd>
              </div>
              <div>
                <dt>Client</dt>
                <dd>{project.client}</dd>
              </div>
              <div>
                <dt>Rôle</dt>
                <dd>{project.role}</dd>
              </div>
            </dl>
          </section>

          {project.highlights?.length > 0 && (
            <section className="fiche-tile fiche-tile--wide">
              <h2 className="fiche-label">Points clés</h2>
              <ul className="fiche-list">
                {project.highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="fiche-tile">
            <h2 className="fiche-label">Outils</h2>
            <ul className="fiche-tags">
              {project.tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          </section>

          {project.process?.length > 0 && (
            <section className="fiche-tile fiche-tile--full">
              <h2 className="fiche-label">Démarche</h2>
              <ol className="fiche-process">
                {project.process.map((step) => (
                  <li key={step.title}>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {project.outcome && (
            <section className="fiche-tile fiche-tile--full">
              <h2 className="fiche-label">Résultat</h2>
              <p className="fiche-text">{project.outcome}</p>
            </section>
          )}

          {project.links?.length > 0 && (
            <section className="fiche-tile fiche-tile--full fiche-links">
              {project.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`fiche-link ${link.primary ? 'is-primary' : ''}`}
                >
                  {link.label}
                  <ArrowUpRight size={18} aria-hidden="true" />
                </a>
              ))}
            </section>
          )}
        </div>
      </div>

      <Link to={`/projets/${next.slug}`} className="project-next">
        <div className="shell project-next-inner">
          <span className="project-next-label">Projet suivant</span>
          <span className="project-next-title">{next.title}</span>
          <ArrowRight size={28} aria-hidden="true" className="project-next-arrow" />
        </div>
      </Link>
    </main>
  );
};

export default ProjectPage;
